<script setup>
import { computed, ref } from 'vue';
import { alertState } from '@/common/commonStatus.js';
import axiosInstance from '@/service/axiosInstance.js';
import {useStore} from "vuex";
import {useRouter} from "vue-router";

const router = useRouter();
const store = useStore();
const emit = defineEmits(['close']);
const isProcessing = ref(false);
const alertList = computed(() => store.state.alertList); // ✅ Vuex에서 알람 리스트 가져오기
import { parseISO, format } from "date-fns";
const formatDate = (isoString) => {
  if (!isoString) return "-";
  const parsed = parseISO(isoString);
  return format(parsed, "MM-dd HH:mm:ss");
};

const sortedAlertList = computed(() => {
  return [...alertList.value].sort((a, b) => {
    return new Date(b.event_date).getTime() - new Date(a.event_date).getTime(); // 최신순
  });
});

const syncAlertsFromServer = async () => {
  // 로컬 상태가 오래됐을 수 있으므로 서버 목록으로 카운트를 정합화한다.
  try {
    const response = await axiosInstance.post('/api/web/v1/event/sseEventList', {}, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    });

    const alerts = response?.data?.body?.alerts || [];
    store.dispatch('setAlertList', alerts);
    store.dispatch('updateAlertCount', alerts.length);
  } catch (error) {
    console.error('❌ 알림 동기화 실패:', error);
  }
};

// ✅ 알람 삭제 및 페이지 이동 (중복 클릭 방지)
const removeAlertAndNavigate = async (alert) => {
   if (!alert || isProcessing.value) return; // ✅ 이미 처리 중이면 무시

   isProcessing.value = true; // ✅ 처리 시작
   emit('close'); // ✅ 클릭 즉시 팝업 닫기
   let pageNum = 0;
   try {
     // 이미 다른 브라우저에서 처리된 알림일 수 있으므로 guid가 있을 때만 삭제를 시도한다.
     if (alert.guid) {
       await axiosInstance.delete(`/api/web/v1/event/remove/${alert.guid}`, {
         withCredentials: true
       });

       // 로컬 리스트 즉시 반영(사용자 체감 개선)
       store.dispatch("removeAlert", alert.guid);
     }

     const data = {
       group_id: alert.group_id,
       enterprise_group_id: alert.enterprise_group_id,
       camera_ip: alert.camera_ip,
       pageSize: 20 // or whatever page size you're using
     };

     // 삭제 성공/실패와 무관하게 페이지 번호를 조회해 이동 정확도를 높인다.
     const pageResponse = await axiosInstance.post('/api/web/v1/event/findAlertPageNum', data, {
       headers: {
         'Content-Type': 'application/json',
       },
       withCredentials: true
     });

     pageNum = pageResponse?.data?.body?.pageNum ?? 0;
   } catch (error) {
     // 핵심 UX: 에러가 나도 이동은 막지 않는다.
     console.warn('⚠️ 알림 처리 중 일부 실패, 기본 페이지로 이동:', error);
   } finally {
     await syncAlertsFromServer();
     isProcessing.value = false; // ✅ 처리 완료
   }

   // 목록 화면에서 동일 그룹/페이지를 바로 열 수 있도록 필터 정보를 함께 전달한다.
   router.push({
     path: '/new/alert/list',
     query: {
       page: pageNum,
       size: 20,
       group_id: alert.group_id,
       enterprise_name: alert.enterprise_name,
       camera_name: alert.camera_name,
       camera_location: alert.camera_location,
       camera_ip: alert.camera_ip,
       enterprise_group_id: alert.enterprise_group_id,
       guid: alert.guid,
     }
   });
};

</script>

<template>
  <div class="alert-list-popup">
    <div class="app-alert-list-wrap">
      <ul v-if="sortedAlertList.length" class="app-alert-list">
        <li v-for="(item, i) in sortedAlertList" :key="i" class="alert-box-item">
          <button
              class="alert-box-btn"
              :class="(alertState[item.alert_warning_level] || alertState['error']).color"
              :disabled="isProcessing"
              @click="removeAlertAndNavigate(item)"
          >
            <div class="alert-box-item-header">
              <Chip class="small"
                    :class="(alertState[item.alert_warning_level] || alertState['error']).class">
                <span>{{ (alertState[item.alert_warning_level] || alertState['error']).label }}</span>
              </Chip>
              <div class="alert-box-item-type">{{ item.camera_name }}</div>
              <div class="alert-box-item-date">{{ formatDate(item.event_date) }}</div>
            </div>
            <div class="alert-box-item-options">
              <div>{{ item.enterprise_name }}</div>
              <div class="spacer"></div>
              <div>{{ item.camera_location }}</div>
            </div>
            <div class="alert-box-info-wrap">
              <div class="desc-item">
                <div class="desc-title">경고 값</div>
                <div class="desc-value">{{ item.alert_warning_value }}({{ item.alert_warning_type }})</div>
              </div>
            </div>
          </button>
        </li>
      </ul>
      <div v-else class="empty-wrap h-min-300">
        <div class="empty-contents">
          <img class="empty-img" src="@/assets/images/empty/no-notification.svg" alt="no-alert">
          <h4 class="empty-title">경고가 없습니다</h4>
        </div>
      </div>
    </div>
  </div>
</template>

